'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * useInlineValidation — a field says what is wrong without shoving the form.
 *
 * WHERE IT COMES FROM. `design/interior/components/inline-validation.tsx` in the
 * forge tree, hook half only. Its styled field is unusable here: 12 `stone-*`,
 * five raw hex, `red-500` / `red-700` / `red-400`, and
 * `shadow-[inset_0_1px_2px_...]`, which is simulated light and was rejected on
 * 2026-09-01.
 *
 * THE JUDGEMENT THAT IS WORTH COPYING, and the only reason this is not written
 * from scratch, is the asymmetry. Validation is silent until the first blur.
 * After that, a value that becomes correct clears IMMEDIATELY and undebounced,
 * while a value that is still wrong waits out the debounce. So the message never
 * flickers once per keystroke, and it never lingers over a field the user has
 * already fixed. Both halves are wrong on their own: debouncing the clear makes
 * a corrected field keep accusing the user, and not debouncing the error makes
 * the message strobe while they type.
 *
 * `debounce = 400` is the number in that file. Copied as a number: the library
 * ships three different spring triples under the single name `DISCLOSE`, so no
 * name from it is trusted here.
 *
 * WHAT IT DOES NOT CARRY, which is the expensive half. The hook contributes
 * exactly one ARIA attribute, `aria-invalid`. The `aria-describedby`
 * composition, the ids, the `role="alert"` node, the reserved 16px box and the
 * reduced-motion branch all live in the styled component and are written by
 * hand here: see `src/components/ui/FieldError.tsx` for the box and the
 * announcement.
 *
 * TWO DEFECTS IN THE SOURCE ARE NOT REPRODUCED.
 *
 *   `commit` fires on blur only there, so a form submitted by keyboard without
 *   ever blurring the field never marks it touched and never shows an error.
 *   `commit()` is exported here and every caller also calls it on submit.
 *
 *   `status: "pending"` is computed and returned there but nothing renders it,
 *   so a wrong field sits for 400ms with no sign anything is happening. Kept in
 *   the return type because it is real information, and used deliberately: it is
 *   what tells a caller that a message is coming, not that none is due.
 */

/** inline-validation.tsx:? — the debounce a still-wrong value waits out. */
export const VALIDATION_DEBOUNCE_MS = 400;

export type InlineValidationStatus = 'idle' | 'pending' | 'valid' | 'invalid';

/**
 * The asymmetry, as a pure function, so it can be tested.
 *
 * This project has no jsdom and no @testing-library/react, so a hook is not
 * directly observable from Vitest (same constraint recorded in
 * `src/components/ui/tabs.test.tsx`). The one piece of judgement worth copying
 * from the source is this decision, so it is the piece that is extracted and
 * driven directly by `src/hooks/__tests__/inlineValidation.test.ts`. The hook
 * below is the only caller.
 *
 *   silent    the field has never been blurred, so it says nothing at all
 *   clear     the value is acceptable, so the message goes NOW, undebounced
 *   debounce  the value is still wrong, so the message waits
 */
export type ValidationDecision =
  | { kind: 'silent' }
  | { kind: 'clear' }
  | { kind: 'debounce'; message: string; after: number };

export function decideValidation({
  touched,
  message,
  debounce = VALIDATION_DEBOUNCE_MS,
}: {
  touched: boolean;
  message: string | null;
  debounce?: number;
}): ValidationDecision {
  if (!touched) return { kind: 'silent' };
  if (message === null) return { kind: 'clear' };
  return { kind: 'debounce', message, after: debounce };
}

export type UseInlineValidationOptions = {
  value: string;
  /** Pure. Returns the message, or null when the value is acceptable. */
  validate: (value: string) => string | null;
  debounce?: number;
  /**
   * An extra value the check depends on. Not in the source, and needed the
   * moment two fields validate against each other: a "passwords must match"
   * rule reads the OTHER field, so editing that one has to re-run this one or
   * the message goes stale. Pass the other value here.
   */
  revalidateOn?: unknown;
};

export function useInlineValidation({
  value,
  validate,
  debounce = VALIDATION_DEBOUNCE_MS,
  revalidateOn,
}: UseInlineValidationOptions) {
  const [touched, setTouched] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /**
   * The last non-empty message, kept after `error` clears so a fading node has
   * something to render on the way out. Nothing here fades today, but dropping
   * it would make the message vanish a frame before the box it sits in.
   */
  const [message, setMessage] = useState('');

  const check = useRef(validate);
  useEffect(() => {
    check.current = validate;
  });

  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clear = useCallback(() => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
  }, []);

  useEffect(() => clear, [clear]);

  useEffect(() => {
    const decision = decideValidation({
      touched,
      message: touched ? check.current(value) : null,
      debounce,
    });

    if (decision.kind === 'silent') return;

    if (decision.kind === 'clear') {
      // Correct again: the message goes on the same keystroke that fixed it,
      // never 400ms later, or the field keeps accusing a user who has complied.
      clear();
      setError(null);
      return;
    }

    // Still wrong: wait it out. This is what stops the message strobing once per
    // keystroke while someone types a password that is not long enough yet.
    clear();
    timer.current = setTimeout(() => {
      setError(decision.message);
      setMessage(decision.message);
    }, decision.after);

    return clear;
  }, [value, touched, debounce, clear, revalidateOn]);

  /**
   * Blur, and submit. The source only wires this to blur, which is how a form
   * completed and submitted from the keyboard shows no error at all.
   */
  const commit = useCallback(() => {
    setTouched(true);
    clear();
    const next = check.current(value);
    setError(next);
    if (next) setMessage(next);
    return next === null;
  }, [value, clear]);

  const reset = useCallback(() => {
    clear();
    setTouched(false);
    setError(null);
    setMessage('');
  }, [clear]);

  const live = touched ? check.current(value) : null;
  const status: InlineValidationStatus = !touched
    ? 'idle'
    : error
      ? 'invalid'
      : live
        ? 'pending'
        : 'valid';

  return {
    status,
    error,
    message,
    touched,
    commit,
    reset,
    fieldProps: {
      onBlur: commit,
      'aria-invalid': error !== null,
    },
  };
}
